/**
 * CrossValidator.ts - Sistema de Validación Cruzada Multi-Fuente
 * 
 * Valida resultados de geocodificación consultando múltiples fuentes
 * y calculando scores compuestos basados en concordancia geográfica.
 * 
 * OBJETIVO: Score 92-98% con detección de errores ~95%
 * 
 * ESTRATEGIA:
 * 1. Obtener resultado de fuente primaria (L0: LOCAL_DERA)
 * 2. Consultar al menos 1 fuente secundaria (L1: WFS online)
 * 3. Calcular distancia entre resultados
 * 4. Generar score compuesto ponderado
 * 5. Detectar discrepancias y flags de revisión
 * 
 * FÓRMULA SCORING:
 * Score = α * C_match + β * C_concordance + γ * C_source
 * Donde:
 *   α = 0.40 (peso mejor match individual)
 *   β = 0.35 (peso concordancia entre fuentes)
 *   γ = 0.25 (peso autoridad de fuentes)
 * 
 * @module services/geocoding/CrossValidator
 * @version 2.0.0 - Integración con algoritmos robustos (huberCentroid, outlier detection)
 * @date 2025-12-04
 */

// NOTA: GeocodingResult se importaba pero no se usa en este módulo

// Errores tipados (F023 Fase 2) - Sistema de discriminated unions
import type {
  GeocodingError,
  ErrorContext,
  InfrastructureTypology,
  GeocodingSource as ErrorGeocodingSource,
} from '../../types/errors';
import {
  createError,
  createErrorContext,
} from '../../types/errors';

// Algoritmos robustos de crossValidation.ts (F023 Fase 2)
// NOTA: huberCentroid, distanceUTM, UTMPoint, ClusterAnalysis preparados para Fase 2
import {
  analyzeResultClusters,
  calculateCompositeScore as calculateEnhancedScore,
  detectDiscrepancy,
  generateRecommendation,
  SOURCE_AUTHORITY_WEIGHTS as ENHANCED_AUTHORITY_WEIGHTS,
  DISCREPANCY_THRESHOLDS,
  type SourceResult as EnhancedSourceResult,
  type GeocodingSourceId,
  type ClusterAnalysis,
  distanceUTM,
  type UTMPoint,
} from '../../lib/crossValidation';

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Fuentes disponibles con sus pesos de autoridad.
 * TD-010 Fase 3: unificado con el tipo canónico del barrel `types/errors`.
 * El tipo local MAYÚSCULAS queda eliminado; todos los módulos usan ahora
 * `lowercase_snake_case` como valores.
 */
export type { GeocodingSource } from '../../types/errors';
// Import local para uso en anotaciones de tipo dentro de este módulo
import type { GeocodingSource } from '../../types/errors';

/** Resultado de una fuente individual */
export interface SourceResult {
  source: GeocodingSource;
  x: number;
  y: number;
  confidence: number;      // 0-100
  matchedName?: string;
  responseTimeMs: number;
}

/** Estado de validación cruzada */
export type ValidationStatus = 
  | 'CONFIRMED'           // <50m entre fuentes, alta confianza
  | 'LIKELY_VALID'        // <200m, confianza media-alta
  | 'UNCERTAIN'           // <500m, requiere atención
  | 'CONFLICT'            // >500m, discrepancia detectada
  | 'SINGLE_SOURCE'       // Solo una fuente disponible
  | 'CADASTRAL_AUTHORITATIVE' // XVC1: parcela DGC exacta (polygon_parcel/refcat); XVAL solo informativa
  | 'NO_RESULTS';         // Ninguna fuente encontró resultado

/** Resultado de validación cruzada */
export interface CrossValidationResult {
  /** Coordenadas finales (mejor estimación) */
  coordinates: {
    x: number;
    y: number;
  } | null;
  
  /** Score compuesto 0-100 */
  compositeScore: number;
  
  /** Estado de validación */
  status: ValidationStatus;
  
  /** Distancia entre fuentes en metros (si hay múltiples) */
  discrepancyMeters: number | null;
  
  /** Resultados de cada fuente consultada */
  sourceResults: SourceResult[];
  
  /** Fuente primaria usada para coordenadas finales */
  primarySource: GeocodingSource | null;
  
  /** Tiempo total de validación en ms */
  totalTimeMs: number;
  
  /** Requiere revisión manual */
  requiresManualReview: boolean;
  
  /** Razón si requiere revisión */
  reviewReason?: string;
  
  /** Errores detectados durante validación (F023 Fase 2) */
  errors?: GeocodingError[];
  
  /** Detalles del cálculo de score */
  scoreBreakdown: {
    C_match: number;
    C_concordance: number;
    C_source: number;
    bonusApplied: number;
  };
}

/** Configuración de validación */
export interface CrossValidationConfig {
  /** Umbral distancia para CONFIRMED (metros) */
  confirmedThreshold: number;
  
  /** Umbral distancia para LIKELY_VALID (metros) */
  likelyValidThreshold: number;
  
  /** Umbral distancia para UNCERTAIN (metros) */
  uncertainThreshold: number;
  
  /** Mínimo de fuentes a consultar */
  minSources: number;
  
  /** Timeout por fuente en ms */
  sourceTimeoutMs: number;
  
  /** Aplicar bonus por concordancia perfecta */
  applyPerfectMatchBonus: boolean;
}

/** Umbrales por tipología de infraestructura */
export interface InfrastructureThresholds {
  confirmed: number;   // metros
  acceptable: number;  // metros
  review: number;      // metros
}

// ============================================================================
// CONSTANTES
// ============================================================================

/** Pesos de autoridad por fuente (0-1) */
export const SOURCE_AUTHORITY_WEIGHTS: Partial<Record<GeocodingSource, number>> = {
  'cdau': 0.95,            // Máxima para direcciones andaluzas
  'wfs_specialized': 0.92, // Muy alta para tipologías específicas
  'wfs_dera': 0.90,        // Alta para datos oficiales Junta
  'local_dera': 0.88,      // Alta pero puede estar desactualizado
  'cartociudad': 0.85,     // Alta cobertura nacional
  'wfs_fallback': 0.82,    // FUT-02: Fallback tipológico (penalizado vs especializado)
  'nomenclator': 0.80,     // NGA IECA — buena para topónimos
  'iaid': 0.78,            // Buena para deportes
  'nominatim': 0.50,       // Fallback, menor precisión (TD-010: absorbe antiguo 'OSM')
  'catastro_ptel': 0.85,   // CAT-12: Alta para refs catastrales verificadas
  'idee': 0.78,            // OPT-03: IDEE/IGN OGC Features
  'catastro_api': 0.82,    // OPT-03: Catastro REST API
};

/** TD-003: Mapea GeocodingSource local a ErrorGeocodingSource para errores tipados */
const mapToErrorSource = (source: GeocodingSource): ErrorGeocodingSource => {
  const mapping: Partial<Record<GeocodingSource, ErrorGeocodingSource>> = {
    'local_dera': 'wfs_dera',
    'wfs_dera': 'wfs_dera',
    'wfs_specialized': 'wfs_dera',
    'wfs_fallback': 'wfs_dera',
    'cdau': 'cdau',
    'cartociudad': 'cartociudad',
    'nomenclator': 'nomenclator',
    'iaid': 'wfs_dera',
    'nominatim': 'nominatim',
    'catastro_ptel': 'manual',
    'idee': 'idee',
    'catastro_api': 'catastro_api',
  };
  return mapping[source] ?? 'manual';
};

/** Pesos del scoring compuesto */
const SCORE_WEIGHTS = {
  α: 0.40,  // Mejor score individual
  β: 0.35,  // Concordancia entre fuentes
  γ: 0.25,  // Autoridad de fuentes
};

/** Configuración por defecto */
const DEFAULT_CONFIG: CrossValidationConfig = {
  confirmedThreshold: 50,      // <50m = CONFIRMED
  likelyValidThreshold: 200,   // <200m = LIKELY_VALID
  uncertainThreshold: 500,     // <500m = UNCERTAIN, >500m = CONFLICT
  minSources: 2,
  sourceTimeoutMs: 5000,
  applyPerfectMatchBonus: true,
};

/** Umbrales por tipo de infraestructura */
export const INFRASTRUCTURE_THRESHOLDS: Record<string, InfrastructureThresholds> = {
  'SANITARIO': { confirmed: 25, acceptable: 50, review: 100 }, // AUDIT_XVAL_SAN_SUBA_20260512 §6.1+§6.2: núcleo bien calibrado (p50=0 m; ≤25 m cubre 83-88 %); p95 alto (CS 232 m, HOSP 115 m) y drift cross-INE (HOSP 6,82 %) neutralizados por guard XVCD aguas arriba — umbrales defensibles. Cierra OBS-FT-XVAL-SAN-P95-OVER-REVIEW.
  'EMERGENCIAS': { confirmed: 25, acceptable: 50, review: 100 },
  'SEGURIDAD': { confirmed: 25, acceptable: 75, review: 150 },
  'BOMBEROS': { confirmed: 25, acceptable: 100, review: 200 }, // L81B: desagregación FIRE post-refutación H₀ F2_SEG_SUBA2 §4.2 (bomberos p95=185,11m estructural por parques en polígonos industriales vs policía/GC p95≈48m; mantener SEGURIDAD{25,75,150} para POLICE; ratio max/min p95 inter-capa = 3,81×)
  'EDUCATIVO': { confirmed: 50, acceptable: 100, review: 200 },
  'ADMINISTRATIVO': { confirmed: 100, acceptable: 200, review: 300 },
  'CULTURAL': { confirmed: 75, acceptable: 150, review: 250 },
  'DEPORTIVO': { confirmed: 100, acceptable: 200, review: 300 },
  'ENERGIA': { confirmed: 150, acceptable: 300, review: 500 },
  'HIDRAULICO': { confirmed: 100, acceptable: 250, review: 400 },

  // M6: Tipologías de área/polígono - umbrales amplios acordes a DISCREPANCY_THRESHOLDS
  'INDUSTRIAL':   { confirmed: 150, acceptable: 300, review: 500 }, // Polígonos industriales
  'COMBUSTIBLE':  { confirmed: 100, acceptable: 250, review: 400 }, // Gasolineras/depósitos
  'VIAL':         { confirmed: 200, acceptable: 400, review: 600 }, // Infraestructura viaria
  'RSU':          { confirmed: 150, acceptable: 350, review: 550 }, // Residuos sólidos urbanos
  'TELECOM':      { confirmed: 100, acceptable: 200, review: 350 }, // Torres/antenas

  'DEFAULT': { confirmed: 100, acceptable: 200, review: 300 },
};

// ============================================================================
// UTILIDADES GEOGRÁFICAS
// ============================================================================

/**
 * Calcula distancia Haversine entre dos puntos en metros
 * Nota: Para coordenadas UTM, usa distancia euclidiana
 */
export function calculateDistance(
  x1: number, y1: number,
  x2: number, y2: number,
  isUTM: boolean = true
): number {
  if (isUTM) {
    // Distancia euclidiana para UTM (ya en metros)
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
  }
  
  // Haversine para coordenadas geográficas (WGS84)
  const R = 6371000; // Radio tierra en metros
  const φ1 = y1 * Math.PI / 180;
  const φ2 = y2 * Math.PI / 180;
  const Δφ = (y2 - y1) * Math.PI / 180;
  const Δλ = (x2 - x1) * Math.PI / 180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c;
}

/**
 * Calcula el centroide ponderado de múltiples puntos
 */
export function calculateWeightedCentroid(
  results: SourceResult[]
): { x: number; y: number } | null {
  if (results.length === 0) return null;
  if (results.length === 1) return { x: results[0].x, y: results[0].y };
  
  let sumX = 0;
  let sumY = 0;
  let sumWeights = 0;
  
  for (const result of results) {
    const weight = (result.confidence / 100) * (SOURCE_AUTHORITY_WEIGHTS[result.source] ?? 0);
    sumX += result.x * weight;
    sumY += result.y * weight;
    sumWeights += weight;
  }
  
  if (sumWeights === 0) return null;
  
  return {
    x: sumX / sumWeights,
    y: sumY / sumWeights,
  };
}

// ============================================================================
// CÁLCULO DE SCORES
// ============================================================================

/**
 * Calcula el score de concordancia basado en distancia
 */
function calculateConcordanceScore(
  distanceMeters: number,
  config: CrossValidationConfig
): number {
  if (distanceMeters <= config.confirmedThreshold) {
    return 1.0; // Concordancia perfecta
  } else if (distanceMeters <= config.likelyValidThreshold) {
    // Interpolación lineal 1.0 → 0.7
    const ratio = (distanceMeters - config.confirmedThreshold) / 
                  (config.likelyValidThreshold - config.confirmedThreshold);
    return 1.0 - (ratio * 0.3);
  } else if (distanceMeters <= config.uncertainThreshold) {
    // Interpolación lineal 0.7 → 0.3
    const ratio = (distanceMeters - config.likelyValidThreshold) / 
                  (config.uncertainThreshold - config.likelyValidThreshold);
    return 0.7 - (ratio * 0.4);
  } else {
    // >500m: penalización fuerte
    return Math.max(0.1, 0.3 - (distanceMeters - config.uncertainThreshold) / 5000);
  }
}

/**
 * Calcula el score compuesto de validación cruzada
 */
export function calculateCompositeScore(
  results: SourceResult[],
  distanceMeters: number | null,
  config: CrossValidationConfig = DEFAULT_CONFIG
): {
  score: number;
  breakdown: CrossValidationResult['scoreBreakdown'];
} {
  if (results.length === 0) {
    return {
      score: 0,
      breakdown: { C_match: 0, C_concordance: 0, C_source: 0, bonusApplied: 0 },
    };
  }
  
  // C_match: Mejor score individual normalizado
  const bestScore = Math.max(...results.map(r => r.confidence));
  const C_match = bestScore / 100;
  
  // C_concordance: Basado en distancia entre fuentes
  let C_concordance: number;
  if (results.length === 1 || distanceMeters === null) {
    C_concordance = 0.5; // Penalización por fuente única
  } else {
    C_concordance = calculateConcordanceScore(distanceMeters, config);
  }
  
  // C_source: Score ponderado por autoridad de fuentes
  let sumWeightedScores = 0;
  let sumWeights = 0;
  for (const result of results) {
    const authority = SOURCE_AUTHORITY_WEIGHTS[result.source] ?? 0;
    sumWeightedScores += (result.confidence / 100) * authority;
    sumWeights += authority;
  }
  const C_source = sumWeights > 0 ? sumWeightedScores / sumWeights : 0;
  
  // Score base
  let score = (SCORE_WEIGHTS.α * C_match + 
               SCORE_WEIGHTS.β * C_concordance + 
               SCORE_WEIGHTS.γ * C_source) * 100;
  
  // Bonus por concordancia perfecta (<25m)
  let bonusApplied = 0;
  if (config.applyPerfectMatchBonus && 
      distanceMeters !== null && 
      distanceMeters <= 25 &&
      results.length >= 2) {
    bonusApplied = Math.min(8, (25 - distanceMeters) / 3);
    score = Math.min(100, score + bonusApplied);
  }
  
  return {
    score: Math.round(score * 10) / 10, // 1 decimal
    breakdown: {
      C_match: Math.round(C_match * 100) / 100,
      C_concordance: Math.round(C_concordance * 100) / 100,
      C_source: Math.round(C_source * 100) / 100,
      bonusApplied: Math.round(bonusApplied * 10) / 10,
    },
  };
}

/**
 * Determina el estado de validación basado en distancia
 */
export function determineValidationStatus(
  results: SourceResult[],
  distanceMeters: number | null,
  config: CrossValidationConfig = DEFAULT_CONFIG
): ValidationStatus {
  if (results.length === 0) {
    return 'NO_RESULTS';
  }
  
  if (results.length === 1) {
    return 'SINGLE_SOURCE';
  }
  
  if (distanceMeters === null) {
    return 'UNCERTAIN';
  }
  
  if (distanceMeters <= config.confirmedThreshold) {
    return 'CONFIRMED';
  } else if (distanceMeters <= config.likelyValidThreshold) {
    return 'LIKELY_VALID';
  } else if (distanceMeters <= config.uncertainThreshold) {
    return 'UNCERTAIN';
  } else {
    return 'CONFLICT';
  }
}

// ============================================================================
// CLASE PRINCIPAL
// ============================================================================

/**
 * Validador cruzado multi-fuente
 */
export class CrossValidator {
  private config: CrossValidationConfig;
  
  constructor(config: Partial<CrossValidationConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }
  
  /**
   * Valida resultados de múltiples fuentes
   */
  public validate(
    results: SourceResult[],
    infrastructureType?: string
  ): CrossValidationResult {
    const startTime = performance.now();
    
    // Filtrar resultados válidos
    const validResults = results.filter(r => 
      r.x !== 0 && r.y !== 0 && r.confidence > 0
    );
    
    // Caso: sin resultados
    if (validResults.length === 0) {
      return this.createEmptyResult(startTime);
    }
    
    // Calcular distancia entre fuentes (si hay múltiples)
    let discrepancyMeters: number | null = null;
    if (validResults.length >= 2) {
      // Calcular distancia máxima entre cualquier par de fuentes
      discrepancyMeters = this.calculateMaxDiscrepancy(validResults);
    }
    
    // Obtener umbrales específicos por tipo (si aplica)
    const thresholds = infrastructureType 
      ? (INFRASTRUCTURE_THRESHOLDS[infrastructureType] || INFRASTRUCTURE_THRESHOLDS['DEFAULT'])
      : INFRASTRUCTURE_THRESHOLDS['DEFAULT'];
    
    // Configuración ajustada por tipo
    const adjustedConfig: CrossValidationConfig = {
      ...this.config,
      confirmedThreshold: thresholds.confirmed,
      likelyValidThreshold: thresholds.acceptable,
      uncertainThreshold: thresholds.review,
    };
    
    // Calcular score compuesto
    const { score, breakdown } = calculateCompositeScore(
      validResults, 
      discrepancyMeters,
      adjustedConfig
    );
    
    // Determinar estado
    const status = determineValidationStatus(
      validResults, 
      discrepancyMeters, 
      adjustedConfig
    );
    
    // Seleccionar coordenadas finales
    const coordinates = this.selectFinalCoordinates(validResults, status);
    
    // Determinar fuente primaria
    const primarySource = this.selectPrimarySource(validResults);
    
    // Determinar si requiere revisión manual
    const { requiresManualReview, reviewReason } = this.checkManualReviewNeeded(
      status, 
      score, 
      discrepancyMeters,
      infrastructureType
    );
    
    // F023 Fase 2: Generar errores tipados según el estado
    const errors = this.generateValidationErrors(
      status,
      validResults,
      discrepancyMeters,
      adjustedConfig.uncertainThreshold,
      infrastructureType
    );
    
    return {
      coordinates,
      compositeScore: score,
      status,
      discrepancyMeters,
      sourceResults: validResults,
      primarySource,
      totalTimeMs: Math.round(performance.now() - startTime),
      requiresManualReview,
      reviewReason,
      errors: errors.length > 0 ? errors : undefined,
      scoreBreakdown: breakdown,
    };
  }
  
  /**
   * Calcula la discrepancia máxima entre fuentes
   */
  private calculateMaxDiscrepancy(results: SourceResult[]): number {
    let maxDistance = 0;
    
    for (let i = 0; i < results.length; i++) {
      for (let j = i + 1; j < results.length; j++) {
        const distance = calculateDistance(
          results[i].x, results[i].y,
          results[j].x, results[j].y,
          true // UTM
        );
        maxDistance = Math.max(maxDistance, distance);
      }
    }
    
    return Math.round(maxDistance * 10) / 10; // 1 decimal
  }
  
  /**
   * Selecciona las coordenadas finales basándose en el estado
   */
  private selectFinalCoordinates(
    results: SourceResult[],
    status: ValidationStatus
  ): { x: number; y: number } | null {
    if (results.length === 0) return null;
    
    switch (status) {
      case 'CONFIRMED':
      case 'LIKELY_VALID':
        // Usar centroide ponderado
        return calculateWeightedCentroid(results);
        
      case 'UNCERTAIN':
      case 'SINGLE_SOURCE':
        // Usar fuente con mayor autoridad
        const bestResult = results.reduce((best, current) => {
          const bestAuthority = (SOURCE_AUTHORITY_WEIGHTS[best.source] ?? 0) * best.confidence;
          const currentAuthority = (SOURCE_AUTHORITY_WEIGHTS[current.source] ?? 0) * current.confidence;
          return currentAuthority > bestAuthority ? current : best;
        });
        return { x: bestResult.x, y: bestResult.y };
        
      case 'CONFLICT':
        // En conflicto, NO devolver coordenadas automáticamente
        // Esto fuerza revisión manual
        return null;
        
      default:
        return null;
    }
  }
  
  /**
   * Selecciona la fuente primaria para atribución
   */
  private selectPrimarySource(results: SourceResult[]): GeocodingSource | null {
    if (results.length === 0) return null;
    
    return results.reduce((best, current) => {
      const bestScore = (SOURCE_AUTHORITY_WEIGHTS[best.source] ?? 0) * best.confidence;
      const currentScore = (SOURCE_AUTHORITY_WEIGHTS[current.source] ?? 0) * current.confidence;
      return currentScore > bestScore ? current : best;
    }).source;
  }
  
  /**
   * Determina si se necesita revisión manual
   */
  private checkManualReviewNeeded(
    status: ValidationStatus,
    score: number,
    discrepancyMeters: number | null,
    infrastructureType?: string
  ): { requiresManualReview: boolean; reviewReason?: string } {
    
    // Conflicto siempre requiere revisión
    if (status === 'CONFLICT') {
      return {
        requiresManualReview: true,
        reviewReason: `Discrepancia de ${discrepancyMeters}m entre fuentes`,
      };
    }
    
    // Score muy bajo
    if (score < 60) {
      return {
        requiresManualReview: true,
        reviewReason: `Score bajo (${score}%)`,
      };
    }
    
    // Fuente única para tipologías críticas
    if (status === 'SINGLE_SOURCE') {
      const criticalTypes = ['SANITARIO', 'EMERGENCIAS', 'SEGURIDAD', 'BOMBEROS'];
      if (infrastructureType && criticalTypes.includes(infrastructureType)) {
        return {
          requiresManualReview: true,
          reviewReason: `Infraestructura crítica con fuente única`,
        };
      }
    }
    
    // Score medio-bajo en tipologías críticas
    if (score < 80 && infrastructureType) {
      const criticalTypes = ['SANITARIO', 'EMERGENCIAS'];
      if (criticalTypes.includes(infrastructureType)) {
        return {
          requiresManualReview: true,
          reviewReason: `Score ${score}% insuficiente para ${infrastructureType}`,
        };
      }
    }
    
    return { requiresManualReview: false };
  }
  
  /**
   * F023 Fase 2: Genera errores tipados según el estado de validación.
   * 
   * - CONFLICT: Genera DiscrepancyError con detalles de las fuentes
   * - Outliers detectados: Genera OutlierDetectedError por cada outlier
   */
  private generateValidationErrors(
    status: ValidationStatus,
    results: SourceResult[],
    discrepancyMeters: number | null,
    thresholdMeters: number,
    infrastructureType?: string
  ): GeocodingError[] {
    const errors: GeocodingError[] = [];
    const context: ErrorContext = createErrorContext('manual', 'L5', {
      tipologia: infrastructureType as InfrastructureTypology | undefined,
    });
    
    // Generar SourceDiscrepancyError si hay conflicto
    if (status === 'CONFLICT' && discrepancyMeters !== null && results.length >= 2) {
      const sources = results.map(r => ({
        source: mapToErrorSource(r.source),
        coordinates: { x: r.x, y: r.y },
        confidence: r.confidence / 100,
      }));
      
      errors.push(createError.sourceDiscrepancy(
        context,
        sources,
        discrepancyMeters,
        discrepancyMeters, // meanDiscrepancy = maxDiscrepancy simplificado
        undefined, // huberCentroid
        'manual_required'
      ));
    }
    
    return errors;
  }
  
  /**
   * F023 Fase 2: Genera errores tipados para validateEnhanced.
   * 
   * - OutlierDetectedError: Por cada fuente identificada como outlier
   * - DiscrepancyError: Si hay discrepancia significativa entre fuentes
   */
  private generateEnhancedValidationErrors(
    clusterAnalysis: ClusterAnalysis,
    results: SourceResult[],
    enhancedResults: EnhancedSourceResult[],
    discrepancy: { hasDiscrepancy: boolean; reason: string; severity: 'low' | 'medium' | 'high' },
    thresholdMeters: number,
    infrastructureType?: string
  ): GeocodingError[] {
    const errors: GeocodingError[] = [];
    const context: ErrorContext = createErrorContext('manual', 'L5', {
      tipologia: infrastructureType as InfrastructureTypology | undefined,
    });
    
    // Generar OutlierDetectedError por cada outlier
    if (clusterAnalysis.outlierSources.length > 0) {
      for (const outlierId of clusterAnalysis.outlierSources) {
        // Encontrar el resultado correspondiente
        const outlierResult = enhancedResults.find(r => r.sourceId === outlierId);
        if (outlierResult?.result) {
          const outlierCoord = outlierResult.result.coordinates;
          const distFromCentroid = Math.sqrt(
            Math.pow(outlierCoord.x - clusterAnalysis.centroid.x, 2) +
            Math.pow(outlierCoord.y - clusterAnalysis.centroid.y, 2)
          );
          
          errors.push(createError.outlierDetected(
            context,
            outlierCoord,
            'huber', // detectionMethod
            distFromCentroid / thresholdMeters, // anomalyScore normalizado
            thresholdMeters,
            { centroid: clusterAnalysis.centroid, radius: clusterAnalysis.radiusMeters }
          ));
        }
      }
    }
    
    // Generar SourceDiscrepancyError si hay discrepancia severa o media
    if (discrepancy.hasDiscrepancy && 
        (discrepancy.severity === 'high' || discrepancy.severity === 'medium') &&
        results.length >= 2) {
      const sources = results.map(r => ({
        source: mapToErrorSource(r.source),
        coordinates: { x: r.x, y: r.y },
        confidence: r.confidence / 100,
      }));
      
      errors.push(createError.sourceDiscrepancy(
        context,
        sources,
        clusterAnalysis.radiusMeters,
        clusterAnalysis.radiusMeters, // meanDiscrepancy
        clusterAnalysis.centroid, // huberCentroid
        'manual_required'
      ));
    }
    
    return errors;
  }
  
  /**
   * Crea resultado vacío
   */
  private createEmptyResult(startTime: number): CrossValidationResult {
    const context = createErrorContext('manual', 'L5');
    return {
      coordinates: null,
      compositeScore: 0,
      status: 'NO_RESULTS',
      discrepancyMeters: null,
      sourceResults: [],
      primarySource: null,
      totalTimeMs: Math.round(performance.now() - startTime),
      requiresManualReview: true,
      reviewReason: 'Ninguna fuente encontró resultados',
      errors: [
        createError.noResults(context, 'Ninguna fuente encontró resultados'),
      ],
      scoreBreakdown: {
        C_match: 0,
        C_concordance: 0,
        C_source: 0,
        bonusApplied: 0,
      },
    };
  }
  
  // ==========================================================================
  // VALIDACIÓN MEJORADA CON ALGORITMOS ROBUSTOS (F023 Fase 2)
  // ==========================================================================
  
  /**
   * Valida resultados usando algoritmos robustos de crossValidation.ts
   * 
   * Mejoras sobre validate():
   * - huberCentroid: centroide robusto que reduce influencia de outliers
   * - analyzeResultClusters: detección automática de discordancias
   * - detectDiscrepancy: umbrales específicos por tipología
   * - generateRecommendation: USE_RESULT / MANUAL_REVIEW / REJECT
   * 
   * @param results - Resultados de fuentes
   * @param infrastructureType - Tipología PTEL
   * @returns Resultado de validación cruzada mejorado
   */
  public validateEnhanced(
    results: SourceResult[],
    infrastructureType?: string
  ): CrossValidationResult {
    const startTime = performance.now();
    
    // Filtrar resultados válidos
    const validResults = results.filter(r => 
      r.x !== 0 && r.y !== 0 && r.confidence > 0
    );
    
    // Caso: sin resultados
    if (validResults.length === 0) {
      return this.createEmptyResult(startTime);
    }
    
    // Mapear a formato EnhancedSourceResult para crossValidation.ts
    const enhancedResults: EnhancedSourceResult[] = validResults.map(r => ({
      sourceId: this.mapToEnhancedSourceId(r.source),
      sourceName: r.source,
      authorityWeight: ENHANCED_AUTHORITY_WEIGHTS[this.mapToEnhancedSourceId(r.source)] ?? 0.5,
      result: {
        coordinates: { x: r.x, y: r.y },
        matchedName: r.matchedName,
        matchScore: r.confidence,
        confidence: r.confidence,
      },
      error: null, // null en lugar de undefined para cumplir con el tipo
      responseTimeMs: r.responseTimeMs,
      status: 'success' as const,
    }));
    
    // Analizar cluster con algoritmo robusto (huberCentroid)
    const threshold = DISCREPANCY_THRESHOLDS[infrastructureType?.toUpperCase() ?? ''] 
      ?? DISCREPANCY_THRESHOLDS.DEFAULT;
    
    const clusterAnalysis = analyzeResultClusters(enhancedResults, {
      concordanceThreshold: threshold
    });
    
    // Si no hay análisis válido
    if (!clusterAnalysis) {
      return this.createEmptyResult(startTime);
    }
    
    // Detectar discrepancia
    const discrepancy = detectDiscrepancy(clusterAnalysis, infrastructureType);
    
    // Calcular scores
    const avgMatchScore = validResults.reduce((sum, r) => sum + r.confidence, 0) / validResults.length;
    const avgAuthority = enhancedResults
      .filter(r => !clusterAnalysis.outlierSources.includes(r.sourceId))
      .reduce((sum, r) => sum + r.authorityWeight, 0) / 
      Math.max(1, clusterAnalysis.concordantSources);
    
    const compositeScore = calculateEnhancedScore(
      avgMatchScore,
      clusterAnalysis.concordanceScore,
      avgAuthority
    );
    
    // Generar recomendación
    const { recommendation, reason } = generateRecommendation(
      compositeScore,
      discrepancy,
      clusterAnalysis.concordantSources
    );
    
    // Mapear a ValidationStatus
    const status = this.mapRecommendationToStatus(
      recommendation, 
      validResults.length,
      discrepancy.hasDiscrepancy
    );
    
    // Coordenadas: usar centroide robusto de clusterAnalysis
    const coordinates = clusterAnalysis.centroid;
    
    // F023 Fase 2: Generar errores tipados para outliers y discrepancias
    const errors = this.generateEnhancedValidationErrors(
      clusterAnalysis,
      validResults,
      enhancedResults,
      discrepancy,
      threshold,
      infrastructureType
    );
    
    return {
      coordinates,
      compositeScore,
      status,
      discrepancyMeters: clusterAnalysis.radiusMeters,
      sourceResults: validResults,
      primarySource: this.selectPrimarySource(validResults),
      totalTimeMs: Math.round(performance.now() - startTime),
      requiresManualReview: recommendation === 'MANUAL_REVIEW' || recommendation === 'REJECT',
      reviewReason: recommendation !== 'USE_RESULT' ? reason : undefined,
      errors: errors.length > 0 ? errors : undefined,
      scoreBreakdown: {
        C_match: avgMatchScore,
        C_concordance: Math.round(clusterAnalysis.concordanceScore * 100),
        C_source: Math.round(avgAuthority * 100),
        bonusApplied: 0,
      },
    };
  }
  
  /**
   * Mapea fuente local a GeocodingSourceId de crossValidation.ts
   */
  private mapToEnhancedSourceId(source: GeocodingSource): GeocodingSourceId {
    const mapping: Partial<Record<GeocodingSource, GeocodingSourceId>> = {
      'local_dera': 'local_dera',
      'wfs_dera': 'local_dera',
      'wfs_specialized': 'wfs_sicess',   // Genérico a health
      'wfs_fallback': 'wfs_sicess',      // FUT-02: Fallback tipológico
      'cdau': 'cdau',
      'cartociudad': 'cartociudad',
      'nomenclator': 'nomenclator',
      'iaid': 'wfs_iaph',               // Mapear deportes a cultural
      'nominatim': 'nominatim',
      'catastro_ptel': 'cdau',           // CAT-12: Catastro PTEL → autoridad similar a CDAU
      'idee': 'idee',                    // OPT-03: Directo
      'catastro_api': 'catastro_r2',     // OPT-03: Catastro API → agrupa con Catastro R2
    };
    return mapping[source] ?? 'nominatim';
  }
  
  /**
   * Mapea recomendación a ValidationStatus
   */
  private mapRecommendationToStatus(
    recommendation: 'USE_RESULT' | 'MANUAL_REVIEW' | 'REJECT',
    sourceCount: number,
    hasDiscrepancy: boolean
  ): ValidationStatus {
    if (sourceCount === 1) return 'SINGLE_SOURCE';
    if (recommendation === 'REJECT') return 'CONFLICT';
    if (recommendation === 'MANUAL_REVIEW') {
      return hasDiscrepancy ? 'UNCERTAIN' : 'LIKELY_VALID';
    }
    // USE_RESULT
    return hasDiscrepancy ? 'LIKELY_VALID' : 'CONFIRMED';
  }
  
  /**
   * T3 — Motor de triangulación acumulativa post-oleada-2.
   *
   * Calcula un score compuesto a partir de los resultados R2 acumulados en
   * `sourceResults` (post-oleada-2). Si ≥ `minSources` fuentes con coordenadas
   * válidas convergen dentro de `radiusMeters`, devuelve la media ponderada de
   * confianza de esas fuentes (cap en 92). Si no hay convergencia suficiente,
   * devuelve `null`.
   *
   * @param sourceResults   Array de resultados de fuentes (no se muta)
   * @param radiusMeters    Radio de convergencia en metros (típico: 25)
   * @param minSources      Mínimo de fuentes convergentes requeridas (típico: 3)
   * @returns               Score 0-92 si hay convergencia, null en caso contrario
   */
  public computeAccumulatedScore(
    sourceResults: SourceResult[],
    radiusMeters: number,
    minSources: number
  ): number | null {
    // 1. Filtrar resultados con coordenadas válidas
    const valid = sourceResults.filter(
      r => r.x !== 0 && r.y !== 0 && r.confidence > 0
    );

    if (valid.length < minSources) return null;

    // 2. Calcular centroide simple
    const cx = valid.reduce((sum, r) => sum + r.x, 0) / valid.length;
    const cy = valid.reduce((sum, r) => sum + r.y, 0) / valid.length;
    const centroid: UTMPoint = { x: cx, y: cy };

    // 3. Contar fuentes dentro del radio
    const convergentes = valid.filter(r => {
      const d = distanceUTM({ x: r.x, y: r.y }, centroid);
      return d <= radiusMeters;
    });

    if (convergentes.length < minSources) return null;

    // 4. Score = media ponderada de confianza de los convergentes
    const totalConf = convergentes.reduce((sum, r) => sum + r.confidence, 0);
    const rawScore = totalConf / convergentes.length;

    // 5. Cap en 92 — la triangulación R2 nunca supera un match tipológico oficial
    return Math.min(rawScore, 92);
  }

  /**
   * Obtiene configuración actual
   */
  public getConfig(): CrossValidationConfig {
    return { ...this.config };
  }
  
  /**
   * Actualiza configuración
   */
  public updateConfig(updates: Partial<CrossValidationConfig>): void {
    this.config = { ...this.config, ...updates };
  }
}

// ============================================================================
// INSTANCIA SINGLETON
// ============================================================================

let crossValidatorInstance: CrossValidator | null = null;

export function getCrossValidator(): CrossValidator {
  if (!crossValidatorInstance) {
    crossValidatorInstance = new CrossValidator();
  }
  return crossValidatorInstance;
}

// ============================================================================
// EXPORT
// ============================================================================

export default CrossValidator;
